import { LEDD, lesTall, leddFor, type Felt, type LeddNoekkel, type Skjema } from './skjema.ts';

/**
 * Portene ved «Send inn», speilet fra tjenerens `HypothesisForms.Gates` (H1.4) så låsen står
 * på skjermen FØR kallet (`ux-flow-architect`, «Forklarte låser»; H1.4 «Klienten viser låsen
 * før kallet»). Tjeneren er fortsatt autoriteten: 409 `hypothesis_incomplete` håndteres for
 * kappløpet (leietakeren hever minimumet mens siden står åpen).
 *
 * Minimumene (DD-44): tittelen 5, enheten 1, resten leietakerens `minFieldLength`. De måles på
 * trimmet tekst, som tjeneren lagrer. Grensen vises først når den brytes — her, aldri i en
 * etikett.
 */

export const TITTEL_MIN = 5;

export interface Mangel {
  felt: Felt;
  ledd: LeddNoekkel;
  /** Låsens setning — samme ord som ⚠-brikken i setningen, der det finnes en. */
  tekst: string;
}

/** Terskelens fire deler — «Send inn» er låst til alle fire står (H2.2s grønt-kriterium). */
export type Terskeldel = 'tall' | 'retning' | 'enhet' | 'dato';

export function terskelMangler(s: Skjema): Terskeldel[] {
  const ut: Terskeldel[] = [];
  if (lesTall(s.threshold) === undefined) ut.push('tall');
  if (!s.direction) ut.push('retning');
  if (s.unit.trim().length < 1) ut.push('enhet');
  if (!s.testEndsOn) ut.push('dato');
  return ut;
}

const sp = (ledd: LeddNoekkel) => LEDD.find((l) => l.k === ledd)!.sp;

export function porter(s: Skjema, min: number): Mangel[] {
  const ut: Mangel[] = [];
  const krev = (felt: Felt, verdi: string, minst: number, tekst: string) => {
    if (verdi.trim().length < minst) ut.push({ felt, ledd: leddFor(felt), tekst });
  };
  const har = (felt: Felt, verdi: unknown, tekst: string) => {
    if (verdi === undefined || verdi === '') ut.push({ felt, ledd: leddFor(felt), tekst });
  };

  krev('title', s.title, TITTEL_MIN, `Tittelen må ha minst ${TITTEL_MIN} tegn.`);
  krev('change', s.change, min, `Endringen må ha minst ${min} tegn.`);
  krev('audience', s.audience, min, `«For hvem» må ha minst ${min} tegn.`);
  krev('effect', s.effect, min, `«${sp('effekt')}» må ha minst ${min} tegn.`);
  krev('metricName', s.metricName, min, `Måltallet må ha minst ${min} tegn.`);
  // H3.3: med en kilde fra katalogen svarer katalogen for kilden, påstanden og oppdateringen.
  const katalog = Boolean(s.metricSourceId);
  if (!katalog) krev('sourceText', s.sourceText, min, `Kilden må ha minst ${min} tegn.`);
  if (!katalog) har('sourceClaim', s.sourceClaim, 'Si om tallet finnes i kilden.');

  const terskel = terskelMangler(s);
  if (terskel.includes('retning')) ut.push({ felt: 'direction', ledd: 'rett', tekst: 'Terskelen mangler retning: minst eller høyst.' });
  if (terskel.includes('tall')) ut.push({ felt: 'threshold', ledd: 'rett', tekst: s.threshold.trim() ? 'Terskelen må være et tall.' : 'Terskelen mangler et tall.' });
  if (terskel.includes('enhet')) ut.push({ felt: 'unit', ledd: 'rett', tekst: 'Terskelen mangler en enhet, f.eks. «%».' });

  har('baselineState', s.baselineState, 'Si om tallet i dag er målt.');
  if (s.baselineState === 'maalt' && lesTall(s.baselineValue) === undefined) {
    ut.push({ felt: 'baselineValue', ledd: 'idag', tekst: s.baselineValue.trim() ? 'Tallet i dag må være et tall.' : 'Tallet i dag mangler.' });
  }
  har('baselineOn', s.baselineOn, 'Tallet i dag mangler en dato.');
  if (!katalog) har('updateFrequency', s.updateFrequency, 'Si hvor ofte tallet oppdateres.');
  if (terskel.includes('dato')) ut.push({ felt: 'testEndsOn', ledd: 'rett', tekst: 'Terskelen mangler en dato.' });

  // «Min er en annen» (H3.3): pekeren og teksten krever hverandre. Kortet står ved effekten.
  if (s.supersedesId && s.differenceNote.trim().length < min) ut.push({ felt: 'differenceNote', ledd: 'effekt', tekst: `Skriv hva som er annerledes med din — minst ${min} tegn.` });

  // Valgfritt, men satt og under minimum: tjeneren sier 422 text_too_short.
  const venter = s.whatWeLose.trim();
  if (venter && venter.length < min) ut.push({ felt: 'whatWeLose', ledd: 'venter', tekst: `«${sp('venter')}» må ha minst ${min} tegn, eller stå tom.` });

  // Leddenes rekkefølge, så låsen peker på det første som mangler i skjemaet.
  const rang = (m: Mangel) => LEDD.findIndex((l) => l.k === m.ledd);
  return ut.map((m, i) => ({ m, i })).sort((a, b) => rang(a.m) - rang(b.m) || a.i - b.i).map(({ m }) => m);
}

/** Leddet holder porten: ✓ på raden. Et valgfritt ledd er aldri «mangler». */
export const leddFylt = (mangler: Mangel[], ledd: LeddNoekkel): boolean => !mangler.some((m) => m.ledd === ledd);

/**
 * Låsens grunn som ÉN setning ved den låste knappen (DD-58, `aria-describedby`). Navnet først:
 * uten det sier tjeneren 409 `display_name_missing` uansett hvor komplett formen er.
 */
export function laasgrunn(mangler: Mangel[], { harNavn }: { harNavn: boolean }): string | null {
  if (!harNavn) return 'Sett visningsnavnet ditt før du sender inn.';
  const [foerste, ...resten] = mangler;
  if (!foerste) return null;
  if (resten.length === 0) return foerste.tekst;
  return `${foerste.tekst} ${resten.length === 1 ? 'Én ting til mangler.' : `${resten.length} ting til mangler.`}`;
}
